<template>
    <vx-graph
        v-bind="$attrs"
        v-on="$listeners"
        type="bar"
        ref="bar"
        class="vx-bar-graph"
        :series="series"
        :config="chartOptions"
        :show-data-labels="showDataLabels"
        @dataPointSelection="onDataPointSelected"
    >
        <template #tooltip="props" v-if="$scopedSlots.tooltip">
            <slot name="tooltip" v-bind="props" />
        </template>
    </vx-graph>
</template>

<script>
import chartMixins from './chartMixins.js'

export default {
    name: 'vx-bar-graph',
    mixins: [chartMixins],
    props: {
        horizontal: {
            type: Boolean,
            default: false
        },
        stacked: {
            type: Boolean,
            default: false
        },
        fullInterface: {
            type: Boolean,
            default: false
        },
        noGrid: {
            type: Boolean,
            default: false
        },
        barWidth: {
            type: String,
            default: '70%'
        },
        minValue: {
            type: Number,
            default: 0
        },
        showDataLabels: {
            type: Boolean,
            default: false
        },
        xAnnotations: {
            type: Array,
            default: () => []
        },
        yAnnotations: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        showLegend() {
            return !this.isSingleSeries && this.fullInterface
        },
        chartOptions() {
            return {
                chart: {
                    stacked: this.stacked
                },
                annotations: {
                    xaxis: this.xAnnotations || [],
                    yaxis: this.yAnnotations || []
                },
                yaxis: this.yAxis,
                xaxis: this.xAxis,
                plotOptions: {
                    bar: {
                        horizontal: this.horizontal,
                        columnWidth: this.barWidth,
                        dataLabels: {
                            show: this.fullInterface,
                            position: this.dataLabelPosition
                        }
                    }
                },
                dataLabels: this.dataLabels,
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                fill: {
                    opacity: 0.8
                },
                grid: {
                    show: !this.noGrid && this.fullInterface,
                    xaxis: {
                        lines: {
                            show: this.horizontal && this.xAxis.show
                        }
                    },
                    yaxis: {
                        lines: {
                            show: !this.horizontal && this.yAxis.show
                        }
                    }
                },
                legend: {
                    show: this.showLegend,
                    position: 'bottom'
                }
            }
        },
        dataLabelPosition() {
            return this.horizontal ? 'bottom' : 'top'
        },
        labelColor() {
            return this.isThemeDark ? '#ffffff' : '#313131'
        },
        dataLabels() {
            var dataLabels = {
                show: false,
                offsetX: 0,
                offsetY: -18,
                style: {
                    colors: [this.labelColor]
                }
            }
            if (this.horizontal) {
                dataLabels.offsetX = 12
                dataLabels.offsetY = 0
            }
            return dataLabels
        },
        xAxis() {
            return this.horizontal ? this.yAxis_x : this.xAxis_x
        },
        xAxis_x() {
            var isShown = this.fullInterface
            return {
                show: isShown,
                labels: {
                    show: isShown,
                    formatter: this.formatValue
                },
                axisTicks: {
                    show: isShown
                },
                axisBorder: {
                    show: isShown
                }
            }
        },
        maximumBarValue() {
            if (!this.stacked) {
                return this.allDataPoints.max((x) => x.value)
            }
            var grouped = this.allDataPoints.groupBy((dataPoint) => dataPoint.name)
            return Object.values(grouped).max((group) => group.sum((x) => x.value))
        },
        yAxis() {
            return this.horizontal ? this.xAxis_x : this.yAxis_x
        },
        yAxis_x() {
            var isShown = this.fullInterface && !this.showDataLabels
            return {
                show: isShown,
                min: this.minValue,
                max: this.maximumBarValue * 1.03, // creates small gap, enough for the label to sit above the column
                labels: {
                    show: isShown,
                    formatter: this.formatValue
                },
                axisTicks: {
                    show: isShown
                },
                axisBorder: {
                    show: this.fullInterface
                }
            }
        }
    },
    methods: {
        onDataPointSelected(e, ctx, config) {
            this.$nextTick(() => {
                const selectedElement = this.$el.querySelector(`path[selected='true']`)
                if (!selectedElement) {
                    return
                }

                const seriesElement = selectedElement.parentElement
                const container = seriesElement.parentElement
                const lastSeries = container.querySelectorAll('.apexcharts-series').last()

                container.insertBefore(seriesElement, lastSeries.nextSibling)
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.theme-dark {
    .vx-bar-graph {
        ::v-deep .apexcharts-bar-area {
            &:hover,
            &[selected='true'] {
                stroke: white;
            }
        }
    }
}
.vx-bar-graph {
    ::v-deep .apexcharts-bar-area {
        &:hover,
        &[selected='true'] {
            fill-opacity: 1;
            stroke-width: 3;
            stroke: black;
            filter: none;
        }
    }
}
</style>
